/*
 * A ProgressShell is a UI element comprised of two screens -- a
 * "detail screen" that provides context on what actions a user may take
 * inside the Shell, and an "action screen" that contains interactive elements.
 * These interactive elements may influence what is displayed in other screens
 * inside the Shell.
 *
 * ProgressShells have a concept of "step", which is meant to represent
 * progress. When this step changes, there may be a change in what screens
 * are displayed. The logic controlling how steps affect screens is passed
 * to ProgressShell as props via two functions:
 *
 * - detailScreen, which takes a step parameter and returns an element that
 *   should be shown in the detail-side at that step (note the detailScreen
 *   is not currently able to affect state in the action screen by design), and
 * - actionScreen, which takes a step parameter and an update function (defined
 *   by ProgressShell) and returns an element that should be shown in the
 *   action-side at that step. The update function ought to be called by the
 *   actionScreen whenever an interaction should cause a change to the step.
 */
import * as React from "react";

import type {Referral, Step, UpdateStep} from "@khan/auth-shared";

import ProgressShellLayout from "./progress-shell-layout.tsx";

type Props = {
    referral?: Referral;
    initialStep: Step;
    detailScreen: (step: Step, updateStep: UpdateStep) => React.ReactNode;
    actionScreen: (step: Step, updateStep: UpdateStep) => React.ReactNode;
    // This component will add a bit of padding in order to fit in a page.
    // Sometimes, however, we want to use this component in something like a
    // modal without padding.  Setting this to false will remove the padding.
    //
    // Note that these props are for the default ProgressShellLayout component.
    // If you provide `customLayoutComponent`, then these will be ignored.
    embedded?: boolean;
    // If provided, we'll use this instead of the default ProgressShellLayout
    // component.
    customLayoutComponent?: React.ComponentType<{
        referral?: Referral;
        detailScreenNode: React.ReactNode;
        actionScreenNode: React.ReactNode;
    }>;
};

type State = {
    step: Step;
};

class ProgressShell extends React.Component<Props, State> {
    static defaultProps: {
        embedded: boolean;
    } = {
        embedded: false,
    };

    state: State = {
        step: this.props.initialStep,
    };

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isMounted = false;

    handleUpdateStep: (newStep: Step) => void = (newStep: Step) => {
        this._isMounted && this.setState({step: newStep});
    };

    render(): React.ReactNode {
        const {embedded, customLayoutComponent: CustomLayoutComponent} =
            this.props;

        const detailScreenNode = this.props.detailScreen(
            this.state.step,
            this.handleUpdateStep,
        );
        const actionScreenNode = this.props.actionScreen(
            this.state.step,
            this.handleUpdateStep,
        );

        if (CustomLayoutComponent) {
            return (
                <CustomLayoutComponent
                    referral={this.props.referral || "default"}
                    detailScreenNode={detailScreenNode}
                    actionScreenNode={actionScreenNode}
                />
            );
        }
        return (
            <ProgressShellLayout
                detailScreenNode={detailScreenNode}
                actionScreenNode={actionScreenNode}
                embedded={embedded}
            />
        );
    }
}

export default ProgressShell;
